"use client";

import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

import { v4 as uuidv4 } from "uuid";
import {
  type SearchProduct,
  type Collection as APICollection,
  createCollection as createCollectionAPI,
  deleteCollection as deleteCollectionAPI,
  getCollection as getCollectionAPI,
  listCollectionsForUser,
  updateCollection as updateCollectionAPI,
} from "@/lib/shop-agent-client";

import { useAuth } from "./AuthContext";

// Note: Collections no longer store products directly - that's handled by the cart system
export interface Collection {
  id: string;
  name: string;
  productIds: string[]; // This will be empty as products are now in cart
  description?: string;
  isDefault?: boolean;
  isPublic?: boolean;
  created_at?: string;
  updated_at?: string;
}

interface CollectionsContextType {
  collections: Collection[];
  loading: boolean;
  error: string | null;
  createCollection: (name: string) => Promise<Collection | null>;
  updateCollection: (id: string, updates: Partial<Collection>) => Promise<boolean>;
  deleteCollection: (id: string) => Promise<boolean>;
  addProductToCollection: (collectionId: string, productId: string) => Promise<boolean>;
  removeProductFromCollection: (collectionId: string, productId: string) => Promise<boolean>;
  isProductInCollection?: (collectionId: string, productId: string) => boolean;
  getProductCollections: (productId: string) => string[];
  bulkCheckProductsInCollection: (
    collectionId: string,
    productIds: string[]
  ) => Promise<Record<string, boolean>>;
  getProductsForCollection: (collectionId: string) => Promise<SearchProduct[]>;
  getProductIdsForCollection: (collectionId: string) => string[];
  fetchCollections: () => Promise<void>;
  ensureDefaultCollection: () => Promise<void>;
}

const CollectionsContext = createContext<CollectionsContextType | undefined>(undefined);

// Update convertAPICollection to include productIds
const convertAPICollection = (apiCollection: APICollection): Collection => ({
  id: apiCollection.id,
  name: apiCollection.name,
  productIds: apiCollection.product_ids || [],
  isDefault: apiCollection.is_default || false,
  isPublic: (apiCollection as unknown as { is_public?: boolean }).is_public || false,
  created_at: apiCollection.created_at || undefined,
  updated_at: apiCollection.updated_at || undefined,
});

// Add helper to update collection products remotely
const patchCollectionProducts = (collectionId: string, productIds: string[]) => {
  return updateCollectionAPI(collectionId, { product_ids: productIds });
};

export function CollectionsProvider({ children }: { children: React.ReactNode }) {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, isAuthenticated } = useAuth();

  const fetchCollections = useCallback(async () => {
    // Skip for logged-out or anonymous users - they don't have backend user records
    if (!user || !isAuthenticated) {
      setCollections([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    let convertedCollections: Collection[] = [];

    // 1. Try to fetch existing collections for the user.
    try {
      const apiCollections = await listCollectionsForUser();
      convertedCollections = apiCollections.map(convertAPICollection);
    } catch (err) {
      console.error("Failed to list collections – will attempt to create default:", err);
      // We'll still try to create a default collection below.
    }

    // 2. Migration & dedupe: consolidate legacy collections into one default "All Saves"
    try {
      const legacyNames = new Set(["all saves", "all saved", "saved", "my saved", "my saves"]);
      const collectionsByName = convertedCollections.reduce<Record<string, Collection[]>>(
        (acc, col) => {
          const key = (col.name || "").trim().toLowerCase();
          if (!acc[key]) acc[key] = [];
          acc[key].push(col);
          return acc;
        },
        {}
      );

      const defaultCol = convertedCollections.find((c) => c.isDefault) || null;
      const legacyAllSavesGroups = Array.from(legacyNames)
        .map((n) => collectionsByName[n] || [])
        .flat();

      let primary: Collection | null = defaultCol || null;

      // If no default yet, prefer an existing legacy-named collection as primary
      if (!primary && legacyAllSavesGroups.length > 0) {
        primary = legacyAllSavesGroups[0];
      }

      let mutated = false;

      // If multiple legacy/all-saves-like collections exist, merge into primary and delete extras
      if (legacyAllSavesGroups.length > 1 && primary) {
        const extras = legacyAllSavesGroups.filter((c) => c.id !== primary.id);
        const mergedIds = Array.from(
          new Set([...primary.productIds, ...extras.flatMap((e) => e.productIds || [])])
        );
        await patchCollectionProducts(primary.id, mergedIds);
        for (const dup of extras) {
          try {
            await deleteCollectionAPI(dup.id);
          } catch (e) {
            console.warn("Failed to delete duplicate collection", dup.id, e);
          }
        }
        mutated = true;
      }

      // Ensure primary exists and is properly marked and named
      if (primary) {
        const desiredName = "All Saves";
        const needsNameUpdate = (primary.name || "").trim() !== desiredName;
        const needsDefault = !primary.isDefault;
        if (needsNameUpdate || needsDefault) {
          await updateCollectionAPI(primary.id, {
            name: desiredName,
            is_default: true,
          } as unknown as Parameters<typeof updateCollectionAPI>[1]);
          mutated = true;
        }
      }

      // Re-list after any mutation to get canonical state
      if (mutated) {
        const apiCollections = await listCollectionsForUser();
        convertedCollections = apiCollections.map(convertAPICollection);
      }

      // 3. Ensure a default exists; if not, create one
      const hasDefault = convertedCollections.some((c) => c.isDefault);
      if (!hasDefault) {
        await createCollectionAPI({
          id: uuidv4(),
          name: "All Saves",
          is_default: true,
        });
        const apiCollections = await listCollectionsForUser();
        convertedCollections = apiCollections.map(convertAPICollection);
      }
    } catch (err) {
      console.error("Migration/ensure default All Saves failed:", err);
    }

    setCollections(convertedCollections);
    setLoading(false);
  }, [user, isAuthenticated]);

  const ensureDefaultCollection = useCallback(async () => {
    // Skip for logged-out or anonymous users - they don't have backend user records
    if (!user || !isAuthenticated) return;
    // Delegate to fetchCollections which performs idempotent ensure + migration
    await fetchCollections();
  }, [user, isAuthenticated, fetchCollections]);

  useEffect(() => {
    fetchCollections();
  }, [user, fetchCollections]);

  const createCollection = async (name: string): Promise<Collection | null> => {
    if (!user) return null;

    try {
      const newCollection = await createCollectionAPI({
        id: uuidv4(),
        name,
        is_default: false,
      });

      await fetchCollections(); // Refresh the list
      return convertAPICollection(newCollection);
    } catch (err) {
      console.error("Failed to create collection:", err);
      return null;
    }
  };

  const updateCollection = async (id: string, updates: Partial<Collection>): Promise<boolean> => {
    if (!user) return false;

    try {
      // Allow passing through new backend fields like is_public while keeping types safe
      const payload: Record<string, unknown> = {
        name: updates.name,
        is_default: updates.isDefault,
      };
      if (typeof updates.isPublic === "boolean") {
        Object.assign(payload, { is_public: updates.isPublic });
      }
      await updateCollectionAPI(
        id,
        payload as unknown as Parameters<typeof updateCollectionAPI>[1]
      );
      await fetchCollections(); // Refresh the list
      return true;
    } catch (err) {
      console.error("Failed to update collection:", err);
      return false;
    }
  };

  const deleteCollection = async (id: string): Promise<boolean> => {
    if (!user) return false;

    try {
      await deleteCollectionAPI(id);
      await fetchCollections(); // Refresh the list
      return true;
    } catch (err) {
      console.error("Failed to delete collection:", err);
      return false;
    }
  };

  // These methods are deprecated - products are now managed via cart
  // Enforce: adding to any collection also adds to the default "All Saves" collection
  const addProductToCollection = async (
    collectionId: string,
    productId: string
  ): Promise<boolean> => {
    if (!user) return false;

    try {
      let targetCollection = collections.find((c) => c.id === collectionId);
      if (!targetCollection) {
        // Fallback: fetch the collection directly if not yet in state (freshly created)
        try {
          const apiCol = await getCollectionAPI(collectionId);
          targetCollection = convertAPICollection(apiCol);
        } catch (e) {
          console.error("Failed to fetch target collection for add", collectionId, e);
          return false;
        }
      }

      const defaultCollection = collections.find((c) => c.isDefault) || null;

      const operations: Promise<unknown>[] = [];

      // Ensure product is in the target collection
      if (!targetCollection.productIds.includes(productId)) {
        operations.push(
          patchCollectionProducts(targetCollection.id, [...targetCollection.productIds, productId])
        );
      }

      // Ensure product is also in the default collection (if different)
      if (
        defaultCollection &&
        defaultCollection.id !== targetCollection.id &&
        !defaultCollection.productIds.includes(productId)
      ) {
        operations.push(
          patchCollectionProducts(defaultCollection.id, [
            ...defaultCollection.productIds,
            productId,
          ])
        );
      }

      if (operations.length === 0) return true;

      await Promise.all(operations);
      await fetchCollections();
      return true;
    } catch (err) {
      console.error("Failed to add product to collection:", err);
      return false;
    }
  };

  const removeProductFromCollection = async (
    collectionId: string,
    productId: string
  ): Promise<boolean> => {
    if (!user) return false;

    try {
      const defaultCollection = collections.find((c) => c.isDefault) || null;
      const isDefault = defaultCollection?.id === collectionId;

      if (isDefault) {
        // Removing from default unsaves from ALL collections
        const ops: Promise<unknown>[] = [];
        for (const col of collections) {
          if (col.productIds.includes(productId)) {
            const nextIds = col.productIds.filter((id) => id !== productId);
            ops.push(patchCollectionProducts(col.id, nextIds));
          }
        }
        if (ops.length === 0) return true;
        await Promise.all(ops);
        await fetchCollections();
        return true;
      }

      // Removing from a specific non-default collection does not affect default membership
      const current = collections.find((c) => c.id === collectionId);
      const nextIds = (current?.productIds || []).filter((id) => id !== productId);
      const updated = await patchCollectionProducts(collectionId, nextIds);
      if (updated) {
        await fetchCollections();
        return true;
      }
      return false;
    } catch (err) {
      console.error("Failed to remove product from collection:", err);
      return false;
    }
  };

  const getProductCollections = (productId: string): string[] => {
    return collections.filter((c) => c.productIds.includes(productId)).map((c) => c.id);
  };

  const bulkCheckProductsInCollection = async (
    collectionId: string,
    productIds: string[]
  ): Promise<Record<string, boolean>> => {
    const col = collections.find((c) => c.id === collectionId);
    if (!col) return {};
    const result: Record<string, boolean> = {};
    productIds.forEach((pid) => {
      result[pid] = col.productIds.includes(pid);
    });
    return result;
  };

  const getProductIdsForCollection = (collectionId: string): string[] => {
    const col = collections.find((c) => c.id === collectionId);
    return col ? col.productIds : [];
  };

  const getProductsForCollection = async (collectionId: string) => {
    const ids = getProductIdsForCollection(collectionId);
    if (ids.length === 0) return [];
    const { getCatalogProducts } = await import("@/lib/shop-agent-client");
    try {
      const products = await getCatalogProducts(ids);
      return products;
    } catch (err) {
      console.error("Failed to fetch products for collection", err);
      return [];
    }
  };

  return (
    <CollectionsContext.Provider
      value={{
        collections,
        loading,
        error,
        createCollection,
        updateCollection,
        deleteCollection,
        addProductToCollection,
        removeProductFromCollection,
        getProductCollections,
        bulkCheckProductsInCollection,
        fetchCollections,
        getProductIdsForCollection,
        getProductsForCollection,
        ensureDefaultCollection,
      }}
    >
      {children}
    </CollectionsContext.Provider>
  );
}

export const useCollections = () => {
  const context = useContext(CollectionsContext);
  if (!context) {
    throw new Error("useCollections must be used within a CollectionsProvider");
  }
  return context;
};
